import { Component, Input, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

export enum FileUploadStatus {
  NotUploaded = 'not-uploaded',
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Error = 'error'
}

@Component({
  selector: 'app-template-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './template-file-upload.component.html',
  styleUrls: ['./template-file-upload.component.scss']
})
export class TemplateFileUploadComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Input properties
  @Input() templateName: string = '';
  @Input() templateUrl: string = '';
  @Input() acceptedFormats: string[] = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
  @Input() maxFileSize: number = 10; // MB
  @Input() required: boolean = false;
  @Input() currentFile?: File;
  @Input() disabled: boolean = false;

  // Output events
  @Output() fileSelected = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() templateDownloaded = new EventEmitter<string>();

  // Component state
  status: FileUploadStatus = FileUploadStatus.NotUploaded;
  selectedFile?: File;
  errorMessage: string = '';
  uploadProgress: number = 0;

  // Expose enum for template
  FileUploadStatus = FileUploadStatus;

  ngOnInit() {
    if (this.currentFile) {
      this.selectedFile = this.currentFile;
      this.status = FileUploadStatus.Uploaded;
    }
  }

  /**
   * 下載範本檔案
   */
  downloadTemplate() {
    if (this.templateUrl) {
      try {
        // 以另開視窗的方式開啟範本檔案
        window.open(this.templateUrl, '_blank');
        
        this.templateDownloaded.emit(this.templateUrl);
      } catch (error) {
        console.error('開啟範本失敗:', error);
        this.setError('開啟範本失敗，請稍後再試');
      }
    }
  }

  /**
   * 觸發檔案選擇
   */
  triggerFileSelect() {
    if (!this.disabled) {
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * 處理檔案選擇
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      if (this.validateFile(file)) {
        this.selectedFile = file;
        this.status = FileUploadStatus.Uploaded;
        this.errorMessage = '';
        this.fileSelected.emit(file);
      }
    }
    
    // 清空 input 值，以便重複選擇相同檔案
    input.value = '';
  }

  /**
   * 移除已選擇的檔案
   */
  removeFile() {
    this.selectedFile = undefined;
    this.status = FileUploadStatus.NotUploaded;
    this.errorMessage = '';
    this.uploadProgress = 0;
    this.fileRemoved.emit();
  }

  /**
   * 預覽檔案（如果支援）
   */
  previewFile() {
    if (this.selectedFile) {
      const fileUrl = URL.createObjectURL(this.selectedFile);
      window.open(fileUrl, '_blank');
    }
  }

  /**
   * 驗證檔案
   */
  private validateFile(file: File): boolean {
    // 檢查檔案格式
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const normalizedAcceptedFormats = this.acceptedFormats.map(format => format.toLowerCase());
    
    if (!normalizedAcceptedFormats.includes(fileExtension)) {
      this.setError(`不支援的檔案格式。支援格式：${this.acceptedFormats.join(', ')}`);
      return false;
    }

    // 檢查檔案大小
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > this.maxFileSize) {
      this.setError(`檔案大小超過限制。最大允許 ${this.maxFileSize}MB`);
      return false;
    }

    return true;
  }

  /**
   * 設置錯誤訊息
   */
  private setError(message: string) {
    this.errorMessage = message;
    this.status = FileUploadStatus.Error;
  }

  /**
   * 取得範本檔案名稱
   */
  private getTemplateFileName(): string {
    if (this.templateUrl) {
      const urlParts = this.templateUrl.split('/');
      return urlParts[urlParts.length - 1] || `${this.templateName}.pdf`;
    }
    return `${this.templateName}.pdf`;
  }

  /**
   * 取得檔案大小格式化字串
   */
  getFileSizeString(file: File): string {
    const bytes = file.size;
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 取得檔案格式說明
   */
  getAcceptedFormatsText(): string {
    return this.acceptedFormats.join(', ');
  }

  /**
   * 取得檔案圖示 CSS 類別
   */
  getFileIconClass(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa-file-image';
      default:
        return 'fa-file';
    }
  }
}